import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CharityHeader = ({ charity }) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [isShared, setIsShared] = useState(false);
  const navigate = useNavigate();

  const handleFollow = () => {
    setIsFollowing(!isFollowing);
  };

  const handleShare = () => {
    setIsShared(true);
    setTimeout(() => setIsShared(false), 2000);
  };

  const handleDonate = () => {
    // Scroll to donation section
    document.getElementById('donation-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden shadow-elevation-1">
      {/* Cover Image */}
      <div className="relative h-48 md:h-64 overflow-hidden">
        <Image
          src={charity?.coverImage}
          alt={`${charity?.name} cover`}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 bg-black/20 backdrop-blur-sm text-white hover:bg-black/40"
        >
          <Icon name="ArrowLeft" size={20} />
        </Button>

        {/* Action Buttons */}
        <div className="absolute top-4 right-4 flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleShare}
            className="bg-black/20 backdrop-blur-sm text-white hover:bg-black/40"
          >
            <Icon name={isShared ? "Check" : "Share"} size={20} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleFollow}
            className="bg-black/20 backdrop-blur-sm text-white hover:bg-black/40"
          >
            <Icon name={isFollowing ? "Heart" : "HeartOff"} size={20} />
          </Button>
        </div>
      </div>
      {/* Charity Info */}
      <div className="p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-16 h-16 rounded-lg overflow-hidden border-2 border-white shadow-elevation-1">
                <Image
                  src={charity?.logo}
                  alt={`${charity?.name} logo`}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-card-foreground">{charity?.name}</h1>
                <div className="flex items-center space-x-2 mt-1">
                  <Icon name="MapPin" size={16} className="text-muted-foreground" />
                  <span className="text-muted-foreground">{charity?.location}</span>
                </div>
              </div>
            </div>

            {/* Verification Badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              {charity?.verificationBadges?.map((badge, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-1 px-2 py-1 bg-success/10 text-success rounded-full text-xs font-medium"
                >
                  <Icon name="Shield" size={12} />
                  <span>{badge}</span>
                </div>
              ))}
            </div>

            {/* Mission Statement */}
            <p className="text-muted-foreground leading-relaxed mb-4">
              {charity?.mission}
            </p>

            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{charity?.stats?.totalDonations}</div>
                <div className="text-xs text-muted-foreground">Total Raised</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-secondary">{charity?.stats?.beneficiaries}</div>
                <div className="text-xs text-muted-foreground">Lives Impacted</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-accent">{charity?.stats?.projects}</div>
                <div className="text-xs text-muted-foreground">Active Projects</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-warning">{charity?.stats?.transparencyScore}%</div>
                <div className="text-xs text-muted-foreground">Transparency</div>
              </div>
            </div>
          </div>

          {/* Quick Donate Button */}
          <div className="md:ml-6">
            <Button
              onClick={handleDonate}
              size="lg"
              className="w-full md:w-auto"
              iconName="Heart"
              iconPosition="left"
            >
              Donate Now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CharityHeader;